import React, { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Client } from '../../../types/localStorageModels'
import localStorageEnum from '../../../enums/localStorageEnum'
import { OffreModel } from '../../../modules/offre/types/OffreModel'
import { isEmpty } from 'lodash'
import { getLastRequestUser } from '../../../modules/demandeResi/api/demandeResiApi'
import { DemandeResi } from '../../../modules/demandeResi/utils/demandeResiTypes'
import { RechercheContactContext } from '../../../modules/rechercheContact/components/RechercheContactContext'

export interface SelectionPaneContextProps {
	currentClient?: Client
	currentOffer?: OffreModel
	hasCurrentOffer: boolean
	hasCurrentClient: boolean
	updateCurrentClient: (newCurrentClient: Client) => void
	updateCurrentOffer: (newCurrentOffer: OffreModel) => void
	resetSelection: () => void
	demandeResidentielId: string
}

export const SelectionPaneContext = createContext<SelectionPaneContextProps>({
	hasCurrentOffer: false,
	hasCurrentClient: false,
	updateCurrentClient: () => {
	},
	updateCurrentOffer: () => {
	},
	resetSelection: () => {
	},
	demandeResidentielId: ''
})

interface SelectionPaneProviderProps {
	children: ReactNode
}

export const SelectionPaneProvider: FC<SelectionPaneProviderProps> = ({ children }) => {
	const { resetFiltreContact } = useContext(RechercheContactContext)

	const [currentClient, setCurrentClient] = useState<Client | undefined>()
	const [currentOffer, setCurrentOffer] = useState<OffreModel | undefined>()

	const hasCurrentOffer = useMemo(() => !!currentOffer && !isEmpty(currentOffer), [currentOffer])
	const hasCurrentClient = useMemo(() => !!currentClient && !isEmpty(currentClient), [currentClient])

	const [demandeResidentielId, setDemandeResidentielId] = useState<string>('')

	useEffect(() => {
		const client = localStorage.getItem(localStorageEnum.client_courant)
		const offre = localStorage.getItem(localStorageEnum.offre_courante)

		setCurrentClient(client ? JSON.parse(client) : undefined)
		setCurrentOffer(offre ? JSON.parse(offre) : undefined)
	}, [])

	// Récupération de l'id de la demande du client courant
	useEffect(() => {
		if (hasCurrentClient) {
			getLastRequestUser({
				crm_id: currentClient?.id,
				user: currentClient?.email,
				user_type: currentClient?.prospect_type,
				is_residence: true
			})
				.then((response: any) => {
					const demandeResi: DemandeResi = response as DemandeResi
					setDemandeResidentielId(demandeResi.demande_residentiel_id || '')
				})
		}
	}, [currentClient, hasCurrentClient])

	const updateCurrentClient = (newCurrentClient: Client) => {
		setCurrentClient(newCurrentClient)
		localStorage.setItem(localStorageEnum.client_courant, JSON.stringify(newCurrentClient))
	}

	const updateCurrentOffer = (newCurrentOffer: OffreModel) => {
		setCurrentOffer(newCurrentOffer)
		localStorage.setItem(localStorageEnum.offre_courante, JSON.stringify(newCurrentOffer))
	}

	const resetSelection = () => {
		localStorage.removeItem(localStorageEnum.offre_courante)
		localStorage.removeItem(localStorageEnum.client_courant)
		setCurrentClient(undefined)
		setCurrentOffer(undefined)
		// Reset les filtres de recherche de contact
		resetFiltreContact()
	}

	return (
		<SelectionPaneContext.Provider
			value={{
				currentClient,
				currentOffer,
				hasCurrentOffer,
				hasCurrentClient,
				updateCurrentClient,
				updateCurrentOffer,
				resetSelection,
				demandeResidentielId
			}}
		>
			{children}
		</SelectionPaneContext.Provider>
	)
}